import React from 'react';
import { MiniThreadMatch } from '@dreamai/types';
import { ReactComponent as ConversationsIcon } from '../../../icons/nav-links/conversations.svg';
import { useLinkedThreads } from '../../../contexts/LinkedThreadsContext';
import './linked-thread-button.scss';

interface LinkedThreadButtonProps {
  componentName: string;
  match: MiniThreadMatch;
  className?: string;
  content?: React.ReactNode;
  agentId: string;
}

const LinkedThreadButton: React.FC<LinkedThreadButtonProps> = ({
  componentName,
  match,
  className = '',
  content = <ConversationsIcon />,
  agentId,
}) => {
  const { openThread, getMiniThreadInfo } = useLinkedThreads();

  // Get mini-threads info from context
  const { threadIds, loading } = getMiniThreadInfo(componentName, match);

  const handleClick = () => {
    if (threadIds.length === 1) {
      // Open the single existing thread
      openThread(threadIds[0], componentName, match, agentId);
    } else {
      // Create a new thread
      openThread(null, componentName, match, agentId);
    }
  };

  const getButtonText = () => {
    if (loading) return 'Loading...';
    if (threadIds.length === 0) return 'Start Discussion';
    if (threadIds.length === 1) return 'Continue Discussion';
    return `View Discussions (${threadIds.length})`;
  };

  const getTooltipText = () => {
    if (loading) return 'Loading linked threads...';
    if (threadIds.length === 0) return 'Start a new discussion about this item';
    if (threadIds.length === 1) return 'Continue the existing discussion';
    return `View all ${threadIds.length} discussions about this item`;
  };

  const openAllThreads = () => {
    threadIds.forEach((threadId: string) => {
      openThread(threadId, componentName, match, agentId);
    });
  };

  return (
    <button
      className={`linked-thread-button ${threadIds.length > 0 ? `linked-thread-button--active ${className}--active` : ''} ${className}`}
      onClick={handleClick}
      disabled={loading}
      title={getTooltipText()}
    >
      {content}
      {loading && <div className='linked-thread-button__loading' />}
    </button>
  );
};

export default LinkedThreadButton;
